import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Shield,
  Search,
  Users,
  UserCheck,
  UserX,
  Calendar,
  Plus,
  KeyRound,
  Ban,
  Trash2,
  Copy,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  HelpCircle,
  ChefHat,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { user, language } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0 });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | active | expired
  const [dialog, setDialog] = useState(null); // {type: 'extend'|'cancel'|'reset'|'credentials', member, data}
  const [actionLoading, setActionLoading] = useState(false);
  const [setupStatus, setSetupStatus] = useState(null); // {needs_setup, admin_exists, ...}
  const [seeding, setSeeding] = useState(false);

  // Check setup status FIRST (no auth needed)
  useEffect(() => {
    fetch(`${API_URL}/api/admin/setup-status`)
      .then((r) => r.json())
      .then(setSetupStatus)
      .catch(() => setSetupStatus({ needs_setup: false }));
  }, []);

  // Redirect non-admins ONLY if setup is complete (admin already exists)
  useEffect(() => {
    if (!setupStatus) return;
    if (!setupStatus.needs_setup && user && user.membership_type !== 'admin' && user.membership_type !== 'vip') {
      navigate('/');
    }
  }, [user, navigate, setupStatus]);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/members?limit=200`, {
        credentials: 'include',
      });
      if (!res.ok) {
        toast.error(language === 'sk' ? 'Chyba načítania členov' : 'Chyba načítání členů');
        return;
      }
      const data = await res.json();
      setMembers(data.members || []);
      setStats({
        total: data.total || 0,
        active: data.active_count || 0,
        expired: data.expired_count || 0,
      });
    } catch (e) {
      toast.error(`${e.message || 'Error'}`);
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    if (user && (user.membership_type === 'admin' || user.membership_type === 'vip')) {
      loadMembers();
    }
  }, [user, loadMembers]);

  const filteredMembers = members.filter((m) => {
    const matchesSearch =
      !search ||
      m.email?.toLowerCase().includes(search.toLowerCase()) ||
      m.name?.toLowerCase().includes(search.toLowerCase());
    const matchesFilter =
      filter === 'all' ||
      (filter === 'active' && m.is_active) ||
      (filter === 'expired' && !m.is_active);
    return matchesSearch && matchesFilter;
  });

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(language === 'sk' ? 'sk-SK' : 'cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const doExtend = async (member, days) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/members/${member.user_id}/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ days }),
      });
      if (!res.ok) throw new Error('extend failed');
      toast.success(
        language === 'sk'
          ? `Členstvo ${member.email} predĺžené o ${days} dní`
          : `Členství ${member.email} prodlouženo o ${days} dnů`
      );
      setDialog(null);
      loadMembers();
    } catch (e) {
      toast.error(`${e.message || 'Error'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const doCancel = async (member) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/members/${member.user_id}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('cancel failed');
      toast.success(
        language === 'sk'
          ? `Členstvo ${member.email} zrušené`
          : `Členství ${member.email} zrušeno`
      );
      setDialog(null);
      loadMembers();
    } catch (e) {
      toast.error(`${e.message || 'Error'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const doResetPassword = async (member) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/members/${member.user_id}/reset-password`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('reset failed');
      const data = await res.json();
      setDialog({ type: 'credentials', member, data });
    } catch (e) {
      toast.error(`${e.message || 'Error'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const doDelete = async (member) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/members/${member.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'delete failed');
      }
      toast.success(
        language === 'sk'
          ? `Účet ${member.email} bol vymazaný`
          : `Účet ${member.email} byl smazán`
      );
      setDialog(null);
      loadMembers();
    } catch (e) {
      toast.error(`${e.message || 'Error'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(language === 'sk' ? 'Skopírované' : 'Zkopírováno');
  };

  // SETUP MODE: empty DB, show seed-from-snapshot button (no auth required)
  if (setupStatus && setupStatus.needs_setup && setupStatus.snapshot_available) {
    const doSeed = async () => {
      setSeeding(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/seed-from-snapshot`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'Seed failed');
        }
        const data = await res.json();
        toast.success(
          language === 'sk'
            ? `Hotovo! Načítané: ${Object.entries(data.loaded).map(([k, v]) => `${k}=${v}`).join(', ')}`
            : `Hotovo! Načteno: ${Object.entries(data.loaded).map(([k, v]) => `${k}=${v}`).join(', ')}`
        );
        // Reload setup status & full data
        const ns = await fetch(`${API_URL}/api/admin/setup-status`).then((r) => r.json());
        setSetupStatus(ns);
        // Force user to log in as admin (their session is from old DB)
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } catch (e) {
        toast.error(`${e.message || 'Error'}`);
      } finally {
        setSeeding(false);
      }
    };
    return (
      <div className="min-h-screen bg-cream-subtle flex items-center justify-center p-4" data-testid="admin-setup-page">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-hover border border-border/40 p-8 md:p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-heading mb-3">
            {language === 'sk' ? 'Prvotná inicializácia' : 'Prvotní inicializace'}
          </h1>
          <p className="text-muted-foreground mb-2">
            {language === 'sk'
              ? 'Production databáza je prázdna. Načítajte snímku z preview prostredia.'
              : 'Produkční databáze je prázdná. Načtěte snímek z preview prostředí.'}
          </p>
          <p className="text-xs text-muted-foreground mb-7">
            {language === 'sk'
              ? `Snímka pripravená: ${setupStatus.snapshot_size_kb} KB. Toto môžete spustiť iba raz.`
              : `Snímek připraven: ${setupStatus.snapshot_size_kb} KB. Toto lze spustit pouze jednou.`}
          </p>
          <Button
            onClick={doSeed}
            disabled={seeding}
            size="lg"
            className="bg-primary hover:bg-primary-hover text-white rounded-full px-10 btn-lift shadow-hover"
            data-testid="admin-seed-btn"
          >
            {seeding ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                {language === 'sk' ? 'Načítavam…' : 'Načítám…'}
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                {language === 'sk' ? 'Inicializovať z preview snapshotu' : 'Inicializovat z preview snímku'}
              </>
            )}
          </Button>
          <p className="mt-5 text-xs text-muted-foreground">
            {language === 'sk'
              ? 'Po dokončení vás odhlásime. Prihláste sa znova ako admin.'
              : 'Po dokončení vás odhlásíme. Přihlaste se znovu jako admin.'}
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">
          {language === 'sk' ? 'Prihláste sa prosím.' : 'Přihlaste se prosím.'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream-subtle py-8" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-heading">
                {language === 'sk' ? 'Správa členov' : 'Správa členů'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {language === 'sk'
                  ? 'Prehľad a manuálna správa členských prístupov'
                  : 'Přehled a manuální správa členských přístupů'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/recipes')}
              data-testid="admin-recipes-btn"
              className="gap-2"
            >
              <ChefHat className="w-4 h-4" />
              {language === 'sk' ? 'Recepty' : 'Recepty'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/help')}
              data-testid="admin-help-btn"
              className="gap-2"
            >
              <HelpCircle className="w-4 h-4" />
              {language === 'sk' ? 'Nápoveda' : 'Nápověda'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadMembers}
              disabled={loading}
              data-testid="admin-refresh-btn"
              className="gap-2"
            >
              <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
              {language === 'sk' ? 'Obnoviť' : 'Obnovit'}
            </Button>
          </div>
        </div>

        {/* Re-seed banner removed — snapshot sync is permanently disabled on
            populated DBs to prevent accidentally overwriting real members.
            If a fresh bootstrap is ever needed, drop the DB first and the
            setup-mode screen will offer the snapshot import. */}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 shadow-soft border border-border/40" data-testid="admin-stat-total">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                <Users className="w-5 h-5 text-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {language === 'sk' ? 'Celkovo členov' : 'Celkem členů'}
                </p>
                <p className="font-serif text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-soft border border-border/40" data-testid="admin-stat-active">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {language === 'sk' ? 'Aktívni' : 'Aktivní'}
                </p>
                <p className="font-serif text-2xl font-bold text-secondary">{stats.active}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-soft border border-border/40" data-testid="admin-stat-expired">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                <UserX className="w-5 h-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">
                  {language === 'sk' ? 'Expirovaní' : 'Expirovaní'}
                </p>
                <p className="font-serif text-2xl font-bold text-muted-foreground">{stats.expired}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={language === 'sk' ? 'Hľadať podľa emailu alebo mena…' : 'Hledat podle e-mailu nebo jména…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white rounded-xl"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'active', 'expired'].map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                className={cn('rounded-full', filter === f && 'bg-primary hover:bg-primary-hover')}
                data-testid={`admin-filter-${f}`}
              >
                {f === 'all' && (language === 'sk' ? 'Všetci' : 'Všichni')}
                {f === 'active' && (language === 'sk' ? 'Aktívni' : 'Aktivní')}
                {f === 'expired' && (language === 'sk' ? 'Expirovaní' : 'Expirovaní')}
              </Button>
            ))}
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-white rounded-2xl shadow-soft border border-border/40 overflow-hidden" data-testid="admin-members-table">
          {loading ? (
            <div className="p-12 text-center text-muted-foreground">
              <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
              {language === 'sk' ? 'Načítavam…' : 'Načítám…'}
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              {language === 'sk' ? 'Žiadni členovia nenájdení' : 'Žádní členové nenalezeni'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-5 py-3 font-medium">{language === 'sk' ? 'Člen' : 'Člen'}</th>
                    <th className="px-5 py-3 font-medium">
                      {language === 'sk' ? 'Typ' : 'Typ'}
                    </th>
                    <th className="px-5 py-3 font-medium">
                      {language === 'sk' ? 'Stav' : 'Stav'}
                    </th>
                    <th className="px-5 py-3 font-medium">
                      {language === 'sk' ? 'Expiruje' : 'Expiruje'}
                    </th>
                    <th className="px-5 py-3 font-medium text-right">
                      {language === 'sk' ? 'Akcie' : 'Akce'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((m) => (
                    <tr
                      key={m.user_id}
                      className="border-t border-border/40 hover:bg-muted/20 transition-colors"
                      data-testid={`member-row-${m.user_id}`}
                    >
                      <td className="px-5 py-4">
                        <div>
                          <p className="font-medium text-text-heading">{m.name || '—'}</p>
                          <p className="text-xs text-muted-foreground">{m.email}</p>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={cn(
                            'text-xs font-medium px-2.5 py-0.5 rounded-full',
                            m.membership_type === 'yearly' && 'bg-amber-100 text-amber-700',
                            m.membership_type === 'monthly' && 'bg-primary/10 text-primary',
                            m.membership_type === 'quarterly' && 'bg-secondary/10 text-secondary',
                            m.membership_type === 'admin' && 'bg-purple-100 text-purple-700',
                            (m.membership_type === 'expired' || m.membership_type === 'free') && 'bg-muted text-muted-foreground'
                          )}
                        >
                          {m.membership_type}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        {m.is_active ? (
                          <span className="inline-flex items-center gap-1 text-secondary text-xs font-medium">
                            <CheckCircle className="w-3.5 h-3.5" />
                            {language === 'sk' ? `Aktívny (${m.days_remaining} dní)` : `Aktivní (${m.days_remaining} dnů)`}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-muted-foreground text-xs font-medium">
                            <AlertCircle className="w-3.5 h-3.5" />
                            {language === 'sk' ? 'Neaktívny' : 'Neaktivní'}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {formatDate(m.membership_expires)}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDialog({ type: 'extend', member: m })}
                            className="h-8 px-2 text-xs text-secondary hover:bg-secondary/10"
                            data-testid={`extend-btn-${m.user_id}`}
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            {language === 'sk' ? 'Predĺžiť' : 'Prodloužit'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDialog({ type: 'reset', member: m })}
                            className="h-8 px-2 text-xs hover:bg-muted"
                            data-testid={`reset-btn-${m.user_id}`}
                          >
                            <KeyRound className="w-3.5 h-3.5 mr-1" />
                            {language === 'sk' ? 'Heslo' : 'Heslo'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDialog({ type: 'cancel', member: m })}
                            className="h-8 px-2 text-xs text-destructive hover:bg-destructive/10"
                            disabled={!m.is_active}
                            data-testid={`cancel-btn-${m.user_id}`}
                          >
                            <Ban className="w-3.5 h-3.5 mr-1" />
                            {language === 'sk' ? 'Zrušiť' : 'Zrušit'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDialog({ type: 'delete', member: m })}
                            className="h-8 px-2 text-xs text-destructive hover:bg-destructive/10"
                            disabled={m.membership_type === 'admin' || m.membership_type === 'vip'}
                            data-testid={`delete-btn-${m.user_id}`}
                            title={language === 'sk' ? 'Trvalo vymazať účet' : 'Trvale smazat účet'}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            {language === 'sk' ? 'Vymazať' : 'Smazat'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Extend dialog */}
      <Dialog open={dialog?.type === 'extend'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'sk' ? 'Predĺžiť členstvo' : 'Prodloužit členství'}
            </DialogTitle>
            <DialogDescription>
              {dialog?.member?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            {[
              { days: 30, label: language === 'sk' ? '+ 30 dní (mesiac)' : '+ 30 dnů (měsíc)' },
              { days: 90, label: language === 'sk' ? '+ 90 dní (kvartál)' : '+ 90 dnů (kvartál)' },
              { days: 365, label: language === 'sk' ? '+ 365 dní (rok)' : '+ 365 dnů (rok)' },
              { days: 7, label: language === 'sk' ? '+ 7 dní (trial)' : '+ 7 dnů (trial)' },
            ].map(({ days, label }) => (
              <Button
                key={days}
                variant="outline"
                className="h-auto py-3 flex-col gap-0.5"
                onClick={() => doExtend(dialog.member, days)}
                disabled={actionLoading}
                data-testid={`extend-${days}-btn`}
              >
                <span className="font-serif text-lg font-bold">+{days}</span>
                <span className="text-xs">{label}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={dialog?.type === 'cancel'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'sk' ? 'Zrušiť členstvo?' : 'Zrušit členství?'}
            </DialogTitle>
            <DialogDescription>
              {language === 'sk'
                ? `Týmto okamžite zablokujete prístup pre ${dialog?.member?.email}. Môžete ho neskôr znova predĺžiť.`
                : `Tímto okamžitě zablokujete přístup pro ${dialog?.member?.email}. Můžete jej později znovu prodloužit.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog(null)} disabled={actionLoading}>
              {language === 'sk' ? 'Zrušiť' : 'Zrušit'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => doCancel(dialog.member)}
              disabled={actionLoading}
              data-testid="confirm-cancel-btn"
            >
              {language === 'sk' ? 'Áno, zrušiť prístup' : 'Ano, zrušit přístup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete account dialog (hard delete) */}
      <Dialog open={dialog?.type === 'delete'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              {language === 'sk' ? 'Trvalo vymazať účet?' : 'Trvale smazat účet?'}
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                {language === 'sk'
                  ? `Účet ${dialog?.member?.email} bude úplne odstránený z databázy. Túto akciu nie je možné vrátiť späť.`
                  : `Účet ${dialog?.member?.email} bude zcela odstraněn z databáze. Tuto akci nelze vrátit zpět.`}
              </span>
              <span className="block text-xs text-muted-foreground">
                {language === 'sk'
                  ? 'Tip: Ak chcete len odobrať prístup, použite radšej Zrušiť — účet zostane v zozname.'
                  : 'Tip: Pokud chcete jen odebrat přístup, použijte raději Zrušit — účet zůstane v seznamu.'}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog(null)} disabled={actionLoading}>
              {language === 'sk' ? 'Zrušiť' : 'Zrušit'}
            </Button>
            <Button
              variant="destructive"
              onClick={() => doDelete(dialog.member)}
              disabled={actionLoading}
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              {language === 'sk' ? 'Áno, vymazať trvalo' : 'Ano, smazat trvale'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password confirm dialog */}
      <Dialog open={dialog?.type === 'reset'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'sk' ? 'Vygenerovať nové heslo?' : 'Vygenerovat nové heslo?'}
            </DialogTitle>
            <DialogDescription>
              {language === 'sk'
                ? `Staré heslo pre ${dialog?.member?.email} prestane fungovať. Nové heslo sa zobrazí na ďalšej obrazovke.`
                : `Staré heslo pro ${dialog?.member?.email} přestane fungovat. Nové heslo se zobrazí na další obrazovce.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialog(null)} disabled={actionLoading}>
              {language === 'sk' ? 'Zrušiť' : 'Zrušit'}
            </Button>
            <Button
              onClick={() => doResetPassword(dialog.member)}
              disabled={actionLoading}
              data-testid="confirm-reset-btn"
              className="bg-primary hover:bg-primary-hover"
            >
              {language === 'sk' ? 'Áno, vygenerovať' : 'Ano, vygenerovat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-seed dialog removed — was triggered from a banner that no longer
          renders. Bootstrap from snapshot is still available via the dedicated
          setup-mode screen (see top of this file) when DB is fully empty. */}

      {/* Credentials display dialog */}
      <Dialog open={dialog?.type === 'credentials'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'sk' ? 'Nové prístupové údaje' : 'Nové přístupové údaje'}
            </DialogTitle>
            <DialogDescription>
              {language === 'sk'
                ? 'Skopírujte a odošlite zákazníčke. Staré heslo už neplatí.'
                : 'Zkopírujte a odešlete zákaznici. Staré heslo už neplatí.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Email</p>
              <div className="flex gap-2">
                <code className="flex-1 bg-muted px-3 py-2 rounded-lg text-sm font-mono" data-testid="creds-email">
                  {dialog?.data?.email}
                </code>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(dialog?.data?.email)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {language === 'sk' ? 'Nové heslo' : 'Nové heslo'}
              </p>
              <div className="flex gap-2">
                <code className="flex-1 bg-primary/5 text-primary px-3 py-2 rounded-lg text-sm font-mono font-bold" data-testid="creds-password">
                  {dialog?.data?.new_password}
                </code>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(dialog?.data?.new_password)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              className="bg-primary hover:bg-primary-hover"
              onClick={() => {
                setDialog(null);
                loadMembers();
              }}
            >
              {language === 'sk' ? 'Hotovo' : 'Hotovo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
